export const ALL_ACTIONS = [
  "view",
  "create",
  "edit",
  "delete",
  "approve",
  "adjust",
  "export",
  "configure",
  "search",
  "link",
  "unlink",
  "upload",
];

export const PERMISSION_GROUPS = [
  { key: "core", label: "Core Workspace" },
  { key: "frontdesk", label: "Front Desk & Records" },
  { key: "clinical", label: "Clinical Care" },
  { key: "inpatient", label: "Admission & Wards" },
  { key: "diagnostics", label: "Diagnostics" },
  { key: "pharmacy", label: "Pharmacy & Inventory" },
  { key: "finance", label: "Finance & Scheme" },
  { key: "workforce", label: "Staff & Attendance" },
  { key: "reports", label: "Reports & Learning" },
  { key: "settings", label: "Administration & Settings" },
];

export const MODULES = {
  dashboard: {
    label: "Dashboard",
    group: "core",
    description: "Executive overview and operational summary.",
    actions: ["view"],
  },
  "learning-center": {
    label: "Learning Center",
    group: "core",
    description: "Knowledge base, guides, walkthroughs, and demo learning.",
    actions: ["view", "create", "edit"],
  },
  "demo-mode": {
    label: "Demo Mode",
    group: "core",
    description: "Practice workflows without writing to live records.",
    actions: ["view", "create", "delete"],
  },

  patients: {
    label: "Patients / Medical Records",
    group: "frontdesk",
    description: "Patient registration, cards, categories, and patient history.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  patient: {
    label: "Patient Alias",
    group: "frontdesk",
    description: "Backward-compatible permission key for older patient routes.",
    actions: ["view", "create", "edit", "delete", "export"],
    aliasOf: "patients",
  },
  family: {
    label: "Family Folders",
    group: "frontdesk",
    description: "Family folder conversion, ledger view, and family reports.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  appointments: {
    label: "Appointments",
    group: "frontdesk",
    description: "Appointment booking, queueing, and appointment reports.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  emergency: {
    label: "Emergency / Triage",
    group: "frontdesk",
    description: "ED triage intake, queue, and emergency KPI views.",
    actions: ["view", "create", "edit", "delete", "export"],
  },

  consultations: {
    label: "Consultations / Doctor Clerking",
    group: "clinical",
    description: "Doctor queue, clerking forms, orders, prescriptions, and diagnosis coding.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  "diagnosis-master": {
    label: "Diagnosis Master",
    group: "clinical",
    description: "ICD diagnosis codes, aliases, and clinical search terms.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  "nurses-station": {
    label: "Nurses Station",
    group: "clinical",
    description: "Vitals, visual acuity, ANC, nurse notes, and nursing queue.",
    actions: ["view", "create", "edit", "delete", "export"],
  },

  admission: {
    label: "Admission",
    group: "inpatient",
    description: "Pending admission, active admission, direct admission, and discharge queue.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  wards: {
    label: "Wards",
    group: "inpatient",
    description: "Ward setup, bed management, ward rounds, ward charts, and bed analytics.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },

  lab: {
    label: "Laboratory",
    group: "diagnostics",
    description: "Lab dashboard, worklist, sample collection, result entry, review, and MCS.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  "mcs-settings": {
    label: "MCS Settings",
    group: "diagnostics",
    description: "Organisms, antibiotics, specimen types, and MCS templates.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  radiology: {
    label: "Radiology",
    group: "diagnostics",
    description: "Radiology worklist, templates, reporting, authorization, and export.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  "radiology-pacs": {
    label: "Radiology PACS / DICOM",
    group: "diagnostics",
    description: "Configure PACS, upload DICOM files, search Orthanc, link studies, and open DICOM viewers.",
    actions: ["view", "configure", "search", "link", "unlink", "upload"],
  },

  pharmacy: {
    label: "Pharmacy",
    group: "pharmacy",
    description: "Dispensing, prescriptions, walk-in pharmacy, and pharmacy dashboard.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  inventory: {
    label: "Inventory",
    group: "pharmacy",
    description: "Pharmacy inventory, stock reports, expiry monitoring, and stock adjustments.",
    actions: ["view", "create", "edit", "adjust", "delete", "export"],
  },

  billing: {
    label: "Billing & Payments",
    group: "finance",
    description: "Service fees, invoices, payment collection, walk-in billing, and invoice history.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  expenses: {
    label: "Expenses",
    group: "finance",
    description: "Expense entry, approvals, cost reporting, and exports.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  schemes: {
    label: "Scheme / NHIA Setup",
    group: "finance",
    description: "Scheme registration, covered services, drugs, and scheme patient setup.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  "scheme-desk": {
    label: "Scheme Desk",
    group: "finance",
    description: "Scheme clearance, claims workflow, and scheme desk reports.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },

  staff: {
    label: "Staff Management",
    group: "workforce",
    description: "Staff records, users, role assignment, and staff exports.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  "staff-duty": {
    label: "Duty & Attendance",
    group: "workforce",
    description: "Shift templates, duty plans, rosters, summary reports, and device management.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
  },
  biometrics: {
    label: "Biometrics / Attendance Devices",
    group: "workforce",
    description: "Backward-compatible key for biometric attendance routes.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
    aliasOf: "staff-duty",
  },

  reports: {
    label: "Reports",
    group: "reports",
    description: "Clinical, finance, pharmacy, lab, patient, scheme, and operational reports.",
    actions: ["view", "export"],
  },

  settings: {
    label: "General Settings",
    group: "settings",
    description: "Hospital profile, invoice settings, formats, numbering, and global preferences.",
    actions: ["view", "edit", "export"],
  },
  "roles-permissions": {
    label: "Roles & Permissions",
    group: "settings",
    description: "Create roles, clone templates, and manage access control.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  "admin-audit": {
    label: "Admin Audit",
    group: "settings",
    description: "Review local audit logs, financial corrections, reversals, and cancellations.",
    actions: ["view", "export"],
  },
  "server-management": {
    label: "Server Management",
    group: "settings",
    description: "Manage local server activation, health, LAN access, backups, restore guidance, and appliance checks.",
    actions: ["view", "edit"],
  },
  "sync-status": {
    label: "Sync Status",
    group: "settings",
    description: "Inspect hybrid sync health, outbox rows, conflicts, pull state, and retry actions.",
    actions: ["view", "edit"],
  },
  departments: {
    label: "Departments",
    group: "settings",
    description: "Department/unit setup and maintenance.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
  clinics: {
    label: "Clinics",
    group: "settings",
    description: "Clinic setup, staff clinic assignments, and clinic metadata.",
    actions: ["view", "create", "edit", "delete", "export"],
  },
};

const canonicalModule = (moduleKey) => MODULES[moduleKey]?.aliasOf || moduleKey;

export const moduleLabel = (moduleKey) =>
  MODULES[moduleKey]?.label ||
  String(moduleKey || "")
    .replace(/[-_]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const emptyPermissionMap = () =>
  Object.fromEntries(
    Object.entries(MODULES).map(([m, meta]) => [
      m,
      Object.fromEntries((meta.actions || []).map((a) => [a, false])),
    ])
  );

export const normalizePermissionMap = (map = {}) => {
  const base = emptyPermissionMap();
  for (const [moduleKey, meta] of Object.entries(MODULES)) {
    const source = map?.[moduleKey] || map?.[canonicalModule(moduleKey)] || {};
    for (const action of meta.actions || []) {
      base[moduleKey][action] = source?.[action] === true;
    }
  }
  return base;
};

export const grantAllPermissions = () => {
  const map = emptyPermissionMap();
  for (const [moduleKey, meta] of Object.entries(MODULES)) {
    for (const action of meta.actions || []) map[moduleKey][action] = true;
  }
  return map;
};

export const grantViewPermissions = () => {
  const map = emptyPermissionMap();
  for (const [moduleKey, meta] of Object.entries(MODULES)) {
    if ((meta.actions || []).includes("view")) map[moduleKey].view = true;
  }
  return map;
};

export const arrayToMap = (legacyArray = []) => {
  const map = emptyPermissionMap();
  if (!Array.isArray(legacyArray)) return map;

  for (const item of legacyArray) {
    if (typeof item === "string") {
      const [rawModule, action] = item.split(".");
      const moduleKey = canonicalModule(rawModule);
      if (MODULES[moduleKey]?.actions?.includes(action)) {
        map[moduleKey][action] = true;
      }
      if (MODULES[rawModule]?.actions?.includes(action)) {
        map[rawModule][action] = true;
      }
      continue;
    }

    if (!item || typeof item !== "object") continue;
    const rawModule = item.module || item.moduleKey || item.key;
    const moduleKey = canonicalModule(rawModule);
    const access = Array.isArray(item.access) ? item.access : [];

    for (const action of access) {
      if (MODULES[moduleKey]?.actions?.includes(action)) {
        map[moduleKey][action] = true;
      }
      if (MODULES[rawModule]?.actions?.includes(action)) {
        map[rawModule][action] = true;
      }
    }
  }

  return normalizePermissionMap(map);
};

export const mapToArray = (map = {}) => {
  const normalized = normalizePermissionMap(map);
  const out = [];
  for (const [moduleKey, meta] of Object.entries(MODULES)) {
    if (meta.aliasOf) continue;
    const actions = (meta.actions || []).filter((a) => normalized?.[moduleKey]?.[a]);
    if (actions.length) out.push({ module: moduleKey, access: actions });
  }
  return out;
};

export const canFromMap = (permMap, moduleKey, action = "view") => {
  const canonical = canonicalModule(moduleKey);
  return !!permMap?.[moduleKey]?.[action] || !!permMap?.[canonical]?.[action];
};
